import { ArrowUpRight, ArrowDown } from "lucide-react";
import NetworkCanvas from "@/components/NetworkCanvas";

const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative dark-section overflow-hidden min-h-[100svh] flex flex-col"
    >
      {/* Animated network */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-dot-grid opacity-70" />
        <NetworkCanvas />
        {/* Vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 50% 40%, rgba(9,10,15,0) 0%, rgba(9,10,15,0.85) 100%)",
          }}
        />
        {/* Bottom fade into next section */}
        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-[#090a0f]" />
      </div>

      <div className="relative z-10 max-w-[1380px] mx-auto px-6 lg:px-10 pt-[140px] lg:pt-[180px] pb-20 flex-1 flex flex-col">
        {/* Top meta */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-16 lg:mb-24">
          <span className="kicker" data-testid="hero-kicker">
            <span className="relative inline-flex w-1.5 h-1.5">
              <span className="absolute inset-0 rounded-full bg-[#00e5ff]" />
              <span
                className="absolute inset-0 rounded-full bg-[#00e5ff] animate-ping"
                style={{ animationDuration: "2.4s" }}
              />
            </span>
            Compliance Intelligence Overlay
          </span>
          <span className="font-mono text-[11px] tracking-[0.22em] text-white/40 hidden sm:inline-flex items-center gap-3">
            <span className="h-px w-10 bg-white/20" />
            FOR EU UNIVERSAL BANKS, MID-TIER BANKS &amp; BAAS OPERATORS
          </span>
        </div>

        {/* Headline */}
        <h1
          data-testid="hero-headline"
          className="font-display text-white font-bold tracking-tight text-[44px] sm:text-[68px] lg:text-[96px] leading-[0.95] max-w-[16ch] fade-up"
        >
          Two problems.{" "}
          <span className="relative inline-block">
            <span className="relative z-10">One</span>
            <span
              className="absolute -bottom-1 left-0 right-0 h-[10px] bg-[#0047ff]/40"
              aria-hidden="true"
            />
          </span>
          {" "}intelligent overlay.
        </h1>

        <p
          data-testid="hero-subhead"
          className="mt-8 text-white/70 text-[17px] sm:text-[19px] leading-relaxed max-w-[58ch] fade-up"
          style={{ animationDelay: "120ms" }}
        >
          Sentinel solves two things and only two things for a bank&rsquo;s
          compliance estate: <span className="text-white">false-positive
          reduction across AML and fraud</span>, and{" "}
          <span className="text-white">the missed risk that hides
          when entity resolution and cross-system context are absent</span>.
          Delivered as an overlay &mdash; live in under three months, no rip
          and replace.
        </p>

        {/* CTAs */}
        <div className="mt-12 flex flex-col sm:flex-row gap-3 fade-up" style={{ animationDelay: "240ms" }}>
          <a
            href="/get-started?type=diagnostic&source=hero"
            data-testid="hero-cta-diagnostic"
            className="btn-primary"
          >
            Request a Sentinel diagnostic
            <ArrowUpRight className="w-4 h-4" />
          </a>
          <a
            href="#two-problems"
            data-testid="hero-cta-how"
            className="btn-ghost-dark"
          >
            See the two problems
            <ArrowDown className="w-4 h-4" />
          </a>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom stat row */}
        <div className="mt-24 lg:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {[
            { k: "False Positive ↓", v: "AML & fraud false positives — FPRE + FFPRE" },
            { k: "Missed Risk ↓", v: "Entity resolution + cross-system context" },
            { k: "< 3 mo", v: "Time to live, no rip-and-replace" },
            { k: "0", v: "Black-box models in scoring" },
          ].map((s) => (
            <div
              key={s.v}
              className="bg-[#090a0f] px-6 py-6"
              data-testid={`hero-stat-${s.k.replace(/\W+/g, "")}`}
            >
              <p className="font-display font-bold text-white text-[28px] sm:text-[34px] tracking-tight tabular">
                {s.k}
              </p>
              <p className="mt-1 text-white/55 text-[13px] leading-snug">{s.v}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
